import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, openModal, SpinningLoader, TextInput } from "risa-oasis-ui_v2";
import { v4 as uuidv4 } from "uuid";
import { appealNotRequired } from "../../../api/postCall/appealNotRequired";
import {
  generateAppeal,
  GenerateAppealResponse,
} from "../../../api/postCall/generateAppeal";
import { sendAppeal } from "../../../api/postCall/sendAppeal";
import { updateBigQueryData } from "../../../api/postCall/updateBigQueryData";
import {
  AppealGenerateEligibleDetails,
  isAppealGenerateEligible,
} from "../../../data-model/appealGenerateDetails";
import { OncologySubmissionTableDataModel } from "../../../data-model/tablesData";
import { ModalId } from "../../../enums/modalId";
import { PaStatus } from "../../../enums/paStatus";
import {
  AppealMrnSource,
  buildAppealNotRequiredPayload,
  buildGenerateAppealPayload,
  buildSendAppealPayload,
} from "../../../pages/pharmaOncologySubmission/actionHandlers/onSendToAppealClick";
import { logError } from "../../../utils/customLogger";
import SendToAppealLetterView from "./sendToAppealLetterView";

type SendToAppealModalView = "letter" | "not_required";

interface SendToAppealModalProps {
  rowData: OncologySubmissionTableDataModel;
  onClose: () => void;
  onSuccess?: () => void;
  /** NYCBS STP uses `patient_mrn`; Astera oncology uses sequence. */
  mrnSource?: AppealMrnSource;
}

/**
 * Modal shown after Send to Appeal is clicked. Loads appeal generate data,
 * then renders either the appeal letter review or the not-required feedback form.
 */
const SendToAppealModal = ({
  rowData,
  onClose,
  onSuccess,
  mrnSource = "sequence",
}: SendToAppealModalProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [view, setView] = useState<SendToAppealModalView>("letter");
  const [generateResponse, setGenerateResponse] =
    useState<GenerateAppealResponse | null>(null);
  const [notRequiredReason, setNotRequiredReason] = useState("");
  const [appealReason, setAppealReason] = useState(
    () => rowData.pa_status_reason?.trim() || "",
  );
  const [canReturnToLetter, setCanReturnToLetter] = useState(false);

  const eligibleDetails = useMemo((): AppealGenerateEligibleDetails | null => {
    const details = generateResponse?.details;
    return isAppealGenerateEligible(details) ? details : null;
  }, [generateResponse?.details]);

  const appealLetterText =
    eligibleDetails?.appeal_letter?.trim() || "No appeal letter available.";

  const fetchAppealDetails = useCallback(async () => {
    const payload = buildGenerateAppealPayload(rowData, mrnSource);

    if (!payload.identifier) {
      toast.error("Order identifier is missing.");
      onClose();
      return;
    }

    if (!payload.mrn) {
      toast.error(
        mrnSource === "patient_mrn"
          ? "Patient MRN is missing."
          : "Sequence is missing.",
      );
      onClose();
      return;
    }

    setIsLoading(true);

    try {
      const response = await generateAppeal(payload);
      setGenerateResponse(response);

      if (isAppealGenerateEligible(response.details)) {
        setView("letter");
        setCanReturnToLetter(true);
      } else {
        setView("not_required");
        setCanReturnToLetter(false);
        setNotRequiredReason(response.details?.reason ?? "");
      }
    } catch (error) {
      logError(error as Error, "SendToAppealModal.fetchAppealDetails");
      const message =
        error instanceof Error ? error.message : "Failed to generate appeal.";
      toast.error(message);
      onClose();
    } finally {
      setIsLoading(false);
    }
  }, [mrnSource, onClose, rowData]);

  useEffect(() => {
    openModal(ModalId.SEND_TO_APPEAL_MODAL);
    void fetchAppealDetails();
  }, [fetchAppealDetails]);

  const handleDownloadLetter = () => {
    const downloadUrl = eligibleDetails?.appeal_letter_url;
    if (!downloadUrl) {
      toast.error("Appeal letter download is unavailable.");
      return;
    }

    window.open(downloadUrl, "_blank", "noopener,noreferrer");
  };

  const handleSendAppeal = async () => {
    const payload = buildSendAppealPayload(rowData, appealReason);

    if (!payload.identifier) {
      toast.error("Order identifier is missing.");
      return;
    }

    setIsSubmitting(true);
    try {
      // Sequenced, not parallel: sending the appeal is the non-repeatable write.
      // Only once it succeeds is the PA row marked as pending.
      const response = await sendAppeal(payload);

      // A failed status write must not surface as "Failed to send appeal" —
      // the appeal is already committed and a retry would send it twice.
      try {
        await updateBigQueryData({
          filters: { identifier: payload.identifier },
          update_data: {
            response_status: PaStatus.APPEAL_PENDING,
          },
          request_id: uuidv4(),
        });
      } catch (error) {
        logError(error as Error, "SendToAppealModal.updateResponseStatus");
      }

      toast.success(response.message || "Case sent to appeal!");
      onSuccess?.();
      onClose();
    } catch (error) {
      logError(error as Error, "SendToAppealModal.handleSendAppeal");
      const message =
        error instanceof Error ? error.message : "Failed to send appeal.";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitNotRequired = async () => {
    const reason = notRequiredReason.trim();
    if (!reason) {
      toast.error("Enter a reason for appeal not required.");
      return;
    }

    const payload = buildAppealNotRequiredPayload(rowData, reason);
    if (!payload.identifier) {
      toast.error("Order identifier is missing.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await appealNotRequired(payload);
      toast.success(response.message || "Feedback recorded!");
      onSuccess?.();
      onClose();
    } catch (error) {
      logError(error as Error, "SendToAppealModal.handleSubmitNotRequired");
      const message =
        error instanceof Error
          ? error.message
          : "Failed to submit appeal feedback.";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (view === "not_required") {
      if (canReturnToLetter) {
        setView("letter");
        return;
      }

      onClose();
      return;
    }

    onClose();
  };

  const handleSave = () => {
    if (view === "not_required") {
      void handleSubmitNotRequired();
      return;
    }

    void handleSendAppeal();
  };

  const modalTitle =
    view === "not_required" ? "Appeal Not Required" : "Send to Appeal";

  const cancelText = view === "not_required" ? "Back" : "Cancel";
  const saveButtonText = isSubmitting
    ? view === "not_required"
      ? "Submitting..."
      : "Sending..."
    : view === "not_required"
      ? "Submit Feedback"
      : "Send";

  const disableSave =
    isSubmitting || (view === "not_required" && !notRequiredReason.trim());

  return (
    <Modal
      dialogId={ModalId.SEND_TO_APPEAL_MODAL}
      title={modalTitle}
      onClose={onClose}
      onCancel={handleCancel}
      onSave={handleSave}
      saveButtonText={saveButtonText}
      cancelText={cancelText}
      disableSave={disableSave}
      hideFooter={isLoading}
    >
      {isLoading ? (
        <div className="flex min-h-60 flex-col items-center justify-center gap-3 py-8">
          <SpinningLoader />
          <span className="text-h12 text-primaryGray-2">
            Generating appeal...
          </span>
        </div>
      ) : view === "letter" ? (
        <SendToAppealLetterView
          appealLetterText={appealLetterText}
          canDownload={Boolean(eligibleDetails?.appeal_letter_url)}
          onDownloadClick={handleDownloadLetter}
          appealProbability={eligibleDetails?.appeal_probability}
          appealReason={appealReason}
          onAppealReasonChange={setAppealReason}
        />
      ) : (
        <TextInput
          key={`appeal-not-required-reason-${view}`}
          id="appeal-not-required-reason"
          label="Reason"
          placeholder="Enter Reason for Appeal Not Required"
          defaultValue={notRequiredReason}
          onChange={(event) => setNotRequiredReason(event.value ?? "")}
        />
      )}
    </Modal>
  );
};

export default SendToAppealModal;
